import {PlatformInfo} from './rtc-drm-transform/platformChecks';

/** A short AV1 Main profile string, for the capability checks only (the muxer derives the real one in-band). */
const AV1_MSE_TYPE = 'video/mp4; codecs="av01.0.08M.08"';

/** WebRTC in this window can receive AV1. */
export function webRtcReceivesAv1(win: Window = window): boolean {
  const codecs: {mimeType: string}[] = (win as any).RTCRtpReceiver?.getCapabilities?.('video')?.codecs ?? [];
  return codecs.some(c => c.mimeType.toLowerCase() === 'video/av1');
}

/** Media Source Extensions play AV1 (Safari: with a hardware decoder only, M3 / A17 Pro). */
export function mseHasAv1(win: Window = window): boolean {
  const mse = (win as any).ManagedMediaSource ?? (win as any).MediaSource;
  return !!mse?.isTypeSupported?.(AV1_MSE_TYPE);
}

/**
 * A WebKit bug (VTK-3667): on a Mac with a hardware AV1 decoder, Safari turns WebRTC AV1 on only for the pages that
 * exist when their web process first connects to Safari's GPU process (LibWebRTCCodecs::setHasAV1HardwareDecoder sets
 * it on Page::forEachPage, once per process). A page created later in that process has none: a window.open pop-out,
 * a target=_blank link, Open in New Tab/Window, a second ⌘-click. A ⌘T tab starts a new process and has it. Seen
 * here as Safari playing AV1 through MSE (so the decoder is there) but not offering it to WebRTC.
 *
 * Fixed in WebKit 317986@main (2026-07-27, "[Cocoa] Enable WebRTC AV1 by default", bugs.webkit.org 319884): AV1 is
 * on for every page. In Safari Technology Preview 251, not yet in a Safari release. Everything here tests what the
 * window can do, never the Safari version, so it goes quiet by itself once Safari ships the fix.
 */
export function safariAv1Off(win: Window = window, isSafari = PlatformInfo.isSafari): boolean {
  return isSafari && !webRtcReceivesAv1(win) && mseHasAv1(win);
}

/** What to tell the editor when safariAv1Off and the host streams AV1. */
export const SAFARI_AV1_OFF = 'Safari turned AV1 off for this window (a Safari bug: windows and tabs opened from '
  + 'another page don\'t get it), and this host streams AV1. Show this screen as a tab in the main window, or open the '
  + 'page in a new tab with ⌘T. Updating Safari fixes this once a release includes WebKit\'s fix.';

/** localStorage: the last pop-out of this browser (a window opened from another page) had no WebRTC AV1. */
export const NEW_WINDOWS_LACK_AV1_KEY = 'crd-new-windows-lack-av1';

/**
 * In a pop-out: records whether it got WebRTC AV1, for the session page (newWindowsLackAv1). The session page can't
 * test that itself: it is not a window opened from another page. Only where AV1 decodes at all (MSE); elsewhere
 * there is no AV1 to lose. A Safari with the fix records that its pop-outs have it, which ends the warning.
 */
export function recordPopoutAv1(win: Window = window, storage: Storage = localStorage) {
  if (!mseHasAv1(win)) return;
  try {
    if (webRtcReceivesAv1(win)) storage.removeItem(NEW_WINDOWS_LACK_AV1_KEY);
    else storage.setItem(NEW_WINDOWS_LACK_AV1_KEY, '1');
  } catch {
    // No storage: the session page doesn't warn; the pop-out still says why it has no picture.
  }
}

/** The last pop-out of this browser had no WebRTC AV1 (recordPopoutAv1). */
export function newWindowsLackAv1(storage: Storage = localStorage): boolean {
  try {
    return storage.getItem(NEW_WINDOWS_LACK_AV1_KEY) !== null;
  } catch {
    return false;
  }
}

/** RTP payloads that carry no picture of their own: retransmission and error correction. */
const NOT_A_VIDEO_CODEC = new Set(['rtx', 'red', 'ulpfec', 'flexfec-03']);

/**
 * The video codec an SDP answer settles on (upper case, e.g. AV1, H264): the first of its video payloads that is a
 * codec. null when the answer rejects video (port 0) or has none: the two sides have no video codec in common.
 */
export function answeredVideoCodec(sdp: string): string | null {
  const lines = sdp.split(/\r?\n/);
  const start = lines.findIndex(l => l.startsWith('m=video '));
  if (start < 0) return null;
  const [, port, , ...payloads] = lines[start].split(' ');
  if (port === '0') return null;
  const end = lines.findIndex((l, i) => i > start && l.startsWith('m='));
  const section = lines.slice(start + 1, end < 0 ? undefined : end);
  const names = new Map(section.flatMap(l => {
    const m = /^a=rtpmap:(\d+) ([^/]+)\//.exec(l);
    return m ? [[m[1], m[2]] as const] : [];
  }));
  const codec = payloads.map(p => names.get(p)).find(n => n && !NOT_A_VIDEO_CODEC.has(n.toLowerCase()));
  return codec ? codec.toUpperCase() : null;
}
